import type { ComponentRef, ElementRef } from '@angular/core'

interface ModalElementRefOwner {
  elementRef: ElementRef
}
type ModalSignal = () => ModalElementRefOwner | null | undefined
interface ModalHost {
  modal?: ModalElementRefOwner | ModalSignal
}

/**
 * Resolves the modal element ref from legacy view-query properties or signal-based viewChild helpers.
 * @param {ComponentRef<unknown>} ref - Component reference that may expose a modal element.
 * @returns {ElementRef | null} The resolved ElementRef if present.
 */
const resolveModalElementRef = (ref: ComponentRef<unknown>): ElementRef | null => {
  const instance = ref.instance as ModalHost
  const modal = instance.modal

  if (!modal) {
    return null
  }

  if (typeof modal === 'function') {
    return modal()?.elementRef ?? null
  }

  return modal.elementRef ?? null
}

/**
 * Destroys a modal and removes its root element from the DOM when possible.
 * @param {ComponentRef<unknown>} ref - Component reference to destroy.
 */
export const destroyModal = (ref: ComponentRef<unknown>): void => {
  try {
    const el = resolveModalElementRef(ref)?.nativeElement

    if (el?.parentNode) {
      el.parentNode.removeChild(el)
    }
  } catch (err) {}

  ref.destroy()
}
